import { ACT_NAMES, CARDS, CLASS_CARD_POOLS, CLASSES, ENCOUNTERS, ENEMIES, EVENTS, RELICS } from "./content";
import { generateMap } from "./map";
import { loadPreferences, normalizeSettings } from "../platform/preferences";
import { pick, randomInt, shuffle, stream, weightedPick } from "./rng";
import type {
  CardDefinition,
  CardInstance,
  ClassId,
  CombatState,
  CombatantState,
  Effect,
  EventChoice,
  GameState,
  MapNode,
  RelicInstance,
  RewardState,
  ShopItem,
  StatusKey,
} from "./types";

const VERSION = 1;
const uid = (state: Pick<GameState, "seed" | "rngCounter">, prefix = "card") => `${prefix}-${state.seed}-${state.rngCounter}`;
const clone = <T,>(value: T): T => structuredClone(value);
const value = (statuses: Partial<Record<StatusKey, number>>, key: string): number => Number((statuses as Record<string, number>)[key] ?? 0);
const setValue = (statuses: Partial<Record<StatusKey, number>>, key: string, amount: number) => {
  (statuses as Record<string, number>)[key] = Math.max(0, Math.floor(amount));
};
const addValue = (statuses: Partial<Record<StatusKey, number>>, key: string, amount: number) => setValue(statuses, key, value(statuses, key) + amount);
const hasRelic = (state: GameState, id: string) => state.relics.some((relic) => relic.id === id);

function spendResource(state: GameState, key: "ammo" | "command", requested: number): number {
  const spent = Math.min(Math.max(0, requested), value(state.statuses, key));
  if (spent <= 0) return 0;
  addValue(state.statuses, key, -spent);
  if (key === "ammo" && hasRelic(state, "ghost-map")) state.block += 3;
  if (key === "command" && hasRelic(state, "choir-pin")) state.block += 3;
  return spent;
}

export function makeCardInstance(state: Pick<GameState, "seed" | "rngCounter">, cardId: string, upgraded = false, temporary = false): CardInstance {
  const instance = { uid: uid(state), cardId, upgraded, temporary };
  state.rngCounter += 1;
  return instance;
}

export function createRun(classId: ClassId, seed: string, settings?: GameState["settings"]): GameState {
  const cls = CLASSES[classId];
  const base: GameState = {
    version: VERSION,
    seed,
    rngCounter: 1,
    screen: "map",
    classId,
    act: 1,
    floor: 0,
    hp: cls.maxHp,
    maxHp: cls.maxHp,
    block: 0,
    energy: 3,
    gold: 95,
    deck: [],
    relics: [{ id: cls.startingRelic }],
    statuses: {},
    map: generateMap(seed, 1),
    removeCost: 75,
    stats: {
      combatsWon: 0,
      elitesWon: 0,
      bossesWon: 0,
      cardsPlayed: 0,
      damageDealt: 0,
      damageTaken: 0,
      goldEarned: 0,
      cardsAdded: 0,
      cardsRemoved: 0,
      startedAt: new Date().toISOString(),
    },
    settings: normalizeSettings(settings ?? loadPreferences()),
    tutorialComplete: false,
    message: "Choose a route into the Rust March.",
  };
  base.deck = cls.starterDeck.map((cardId) => makeCardInstance(base, cardId));
  return base;
}

function encounterFor(state: GameState, type: "battle" | "elite" | "boss"): string[] {
  const table = ENCOUNTERS[state.act];
  if (type === "boss") return [table.boss];
  const rng = stream(state.seed, `encounter-${state.act}-${state.floor}-${type}`);
  return pick(type === "elite" ? table.elite : table.normal, rng);
}

function createEnemy(state: GameState, enemyId: string, index: number): CombatantState {
  const def = ENEMIES[enemyId];
  const rng = stream(state.seed, `enemy-hp-${state.act}-${state.floor}-${enemyId}-${index}`);
  const rawHp = randomInt(def.minHp, def.maxHp, rng);
  const hpScale = def.tier === "normal" ? 0.86 : def.tier === "elite" ? 0.86 : 0.72;
  const actScale = def.act === 1 ? 1 : def.act === 2 ? 0.87 : 0.75;
  const difficultyScale = state.settings.difficulty === "story" ? 0.82 : state.settings.difficulty === "ascendant" ? 1.15 : 1;
  const hp = Math.max(1, Math.round(rawHp * hpScale * actScale * difficultyScale));
  return {
    id: `${enemyId}-${index}`,
    definitionId: enemyId,
    name: def.name,
    hp,
    maxHp: hp,
    block: 0,
    statuses: {},
    intentIndex: 0,
    intent: def.intents[0],
    phase: 1,
    alive: true,
  };
}

function drawCards(state: GameState, amount: number): CardInstance[] {
  const combat = state.combat!;
  const drawn: CardInstance[] = [];
  for (let i = 0; i < amount; i += 1) {
    if (!combat.drawPile.length && combat.discardPile.length) {
      const rng = stream(state.seed, `reshuffle-${state.act}-${state.floor}-${combat.turn}-${i}`);
      combat.drawPile = shuffle(combat.discardPile, rng);
      combat.discardPile = [];
      combat.log.unshift("The discard pile folds back into the draw pile.");
    }
    const card = combat.drawPile.pop();
    if (!card) break;
    combat.hand.push(card);
    drawn.push(card);
  }
  return drawn;
}

function startingStatuses(state: GameState): Partial<Record<StatusKey, number>> {
  const result: Partial<Record<StatusKey, number>> = {};
  if (hasRelic(state, "ghost-map")) result.ammo = 3;
  if (hasRelic(state, "split-lens")) result.echo = 1;
  if (hasRelic(state, "choir-pin")) result.command = 2;
  return result;
}

export function startCombat(input: GameState, nodeType: "battle" | "elite" | "boss"): GameState {
  const state = clone(input);
  const rng = stream(state.seed, `combat-shuffle-${state.act}-${state.floor}`);
  const maxEnergy = 3 + (hasRelic(state, "last-witness") ? 1 : 0);
  const enemies = encounterFor(state, nodeType).map((enemyId, index) => createEnemy(state, enemyId, index));
  state.statuses = startingStatuses(state);
  state.block = 0;
  state.energy = maxEnergy + (nodeType === "boss" && hasRelic(state, "second-sun") ? 1 : 0);
  state.combat = {
    enemies,
    drawPile: shuffle(state.deck, rng),
    hand: [],
    discardPile: [],
    exhaustPile: [],
    energy: state.energy,
    maxEnergy,
    turn: 1,
    log: [`Combat begins in ${ACT_NAMES[state.act]}.`],
    cardsPlayedThisTurn: 0,
    attacksPlayedThisTurn: 0,
    skillsPlayedThisTurn: 0,
    powersPlayedThisTurn: 0,
    ended: false,
    victory: false,
    nodeType,
  };
  let openingDraw = 5 + (hasRelic(state, "ash-compass") ? 1 : 0) - (hasRelic(state, "hollow-star") ? 1 : 0);
  if (openingDraw < 1) openingDraw = 1;
  drawCards(state, openingDraw);
  state.screen = "combat";
  state.message = nodeType === "boss" ? "A sovereign intelligence blocks the path." : "Read the intent. Build the turn.";
  return state;
}

export function enterNode(input: GameState, nodeId: string): GameState {
  const state = clone(input);
  const node = state.map.nodes.find((entry) => entry.id === nodeId);
  if (!node || !node.accessible || node.visited) return state;
  state.currentNodeId = nodeId;
  state.floor += 1;
  if (node.type === "battle" || node.type === "elite" || node.type === "boss") return startCombat(state, node.type);
  if (node.type === "shop") {
    state.shop = generateShop(state);
    state.screen = "shop";
  } else if (node.type === "event") {
    state.eventId = pick(Object.keys(EVENTS), stream(state.seed, `event-${state.act}-${state.floor}`));
    state.screen = "event";
  } else if (node.type === "rest") {
    if (hasRelic(state, "glass-ration")) state.hp = Math.min(state.maxHp, state.hp + 5);
    state.screen = "rest";
  } else if (node.type === "treasure") {
    state.pendingTreasure = randomRelic(state, "uncommon");
    state.screen = "treasure";
  }
  return state;
}

function completeCurrentNode(state: GameState): void {
  const current = state.map.nodes.find((node) => node.id === state.currentNodeId);
  if (!current) return;
  current.visited = true;
  current.accessible = false;
  for (const node of state.map.nodes) if (!node.visited) node.accessible = current.connections.includes(node.id);
  state.currentNodeId = undefined;
}

function targetEnemy(state: GameState, id?: string): CombatantState | undefined {
  const living = state.combat?.enemies.filter((enemy) => enemy.alive) ?? [];
  return living.find((enemy) => enemy.id === id) ?? living[0];
}

function applyStatus(target: { statuses: Partial<Record<StatusKey, number>> }, status: string, amount: number): boolean {
  if (amount < 0) {
    addValue(target.statuses, status, amount);
    return true;
  }
  if (["weak", "vulnerable", "burn", "poison", "mark", "fracture"].includes(status) && value(target.statuses, "artifact") > 0) {
    addValue(target.statuses, "artifact", -1);
    return false;
  }
  addValue(target.statuses, status, amount);
  return true;
}

function calculateDamage(state: GameState, base: number, enemy: CombatantState, scalar = 1, direct = true): number {
  let amount = Math.max(0, base + value(state.statuses, "strength"));
  if (value(state.statuses, "weak") > 0) amount = Math.floor(amount * 0.75);
  if (direct && value(enemy.statuses, "vulnerable") > 0) amount = Math.floor(amount * 1.5);
  if (direct && value(enemy.statuses, "mark") > 0) amount += value(enemy.statuses, "mark");
  if (direct && value(enemy.statuses, "fracture") > 0) amount += value(enemy.statuses, "fracture");
  if (direct && value(state.statuses, "firstAttackBonus") > 0 && state.combat!.attacksPlayedThisTurn <= 1) amount += value(state.statuses, "firstAttackBonus");
  if (direct && value(enemy.statuses, "mark") > 0) amount += value(state.statuses, "markHitBonus");
  if (direct && value(state.statuses, "damagePenalty") > 0) amount = Math.floor(amount * (100 - value(state.statuses, "damagePenalty")) / 100);
  amount = Math.floor(amount * scalar);
  return Math.max(0, amount);
}

function hurtEnemy(state: GameState, enemy: CombatantState, amount: number, piercing = false): number {
  if (!enemy.alive || amount <= 0) return 0;
  let remaining = amount;
  if (!piercing && enemy.block > 0) {
    const absorbed = Math.min(enemy.block, remaining);
    enemy.block -= absorbed;
    remaining -= absorbed;
  }
  enemy.hp -= remaining;
  state.stats.damageDealt += remaining;
  if (enemy.hp <= 0) {
    enemy.hp = 0;
    enemy.alive = false;
    state.combat!.log.unshift(`${enemy.name} falls silent.`);
  }
  return remaining;
}

function hurtPlayer(state: GameState, amount: number, source = "damage", piercing = false): number {
  if (amount <= 0) return 0;
  let remaining = amount;
  if (!piercing && state.block > 0) {
    const absorbed = Math.min(state.block, remaining);
    state.block -= absorbed;
    remaining -= absorbed;
  }
  if (remaining > 0) {
    state.hp -= remaining;
    state.stats.damageTaken += remaining;
    state.combat?.log.unshift(`${source}: ${remaining} HP lost.`);
  }
  if (state.hp <= 0 && hasRelic(state, "mercy-clause")) {
    state.hp = 1;
    state.relics = state.relics.filter((relic) => relic.id !== "mercy-clause");
    state.combat?.log.unshift("The Mercy Clause burns away and leaves you at 1 HP.");
  }
  if (state.hp <= 0) {
    state.hp = 0;
    if (state.combat) state.combat.ended = true;
    state.screen = "defeat";
  }
  return remaining;
}

function consumeDirectAmplifiers(state: GameState, enemy: CombatantState): void {
  if (value(enemy.statuses, "mark") > 0) {
    setValue(enemy.statuses, "mark", 0);
    if (value(state.statuses, "markRefund") > value(state.statuses, "markRefundUsed")) {
      addValue(state.statuses, "ammo", 1);
      drawCards(state, 1);
      addValue(state.statuses, "markRefundUsed", 1);
    }
  }
  if (value(enemy.statuses, "fracture") > 0 && value(state.statuses, "persistentFracture") <= 0) {
    setValue(enemy.statuses, "fracture", 0);
    if (hasRelic(state, "split-lens")) state.block += 3;
  }
}

function resolveDamage(state: GameState, effect: Extract<Effect, { kind: "damage" }>, targetId: string | undefined, scalar: number): void {
  const targets = effect.target === "all-enemies" ? state.combat!.enemies.filter((enemy) => enemy.alive) : [targetEnemy(state, targetId)].filter(Boolean) as CombatantState[];
  for (const enemy of targets) {
    const bonus = effect.bonusPer ? value(state.statuses, effect.bonusPer) * (effect.bonusMultiplier ?? 1) : 0;
    const hits = effect.hits ?? 1;
    for (let hit = 0; hit < hits; hit += 1) {
      const amount = calculateDamage(state, effect.amount + bonus, enemy, scalar, true);
      hurtEnemy(state, enemy, amount);
      if (value(enemy.statuses, "thorns") > 0) hurtPlayer(state, value(enemy.statuses, "thorns"), `${enemy.name}'s Thorns`, true);
      if (hit === 0) consumeDirectAmplifiers(state, enemy);
    }
    if (effect.consume) setValue(state.statuses, effect.consume, 0);
  }
}

function drawWithPlating(state: GameState, amount: number, plus: boolean): void {
  const drawn = drawCards(state, amount);
  const skills = drawn.filter((entry) => CARDS[entry.cardId].type === "skill").length;
  addValue(state.statuses, "plating", skills * (plus ? 2 : 1));
}

function resolveSpecial(state: GameState, id: string, amount: number | undefined, targetId: string | undefined, scalar: number): void {
  const combat = state.combat!;
  const enemy = targetEnemy(state, targetId);
  const n = amount ?? 0;
  switch (id) {
    case "block-per-discard": state.block += Math.floor(combat.discardPile.length / Math.max(1, n)); break;
    case "vent-burn": { const burn = value(state.statuses, "burn"); state.block += burn * n; setValue(state.statuses, "burn", 0); break; }
    case "damage-if-block": if (state.block > 0 && enemy) hurtEnemy(state, enemy, calculateDamage(state, n, enemy, scalar)); break;
    case "persistent-plating": addValue(state.statuses, "persistentPlating", n); break;
    case "block-missing-hp": state.block += Math.floor((state.maxHp - state.hp) * n / 100); break;
    case "block-to-damage": if (enemy) { hurtEnemy(state, enemy, Math.floor(state.block * n / 100)); state.block = 0; } break;
    case "draw-plating": drawWithPlating(state, n, false); break;
    case "draw-plating-plus": drawWithPlating(state, n, true); break;
    case "block-retention": setValue(state.statuses, "blockRetention", n); break;
    case "fracture-burst": if (enemy) hurtEnemy(state, enemy, Math.floor(value(enemy.statuses, "fracture") * n * scalar)); break;
    case "steal-strength": if (enemy) { const stolen = Math.min(n, value(enemy.statuses, "strength")); addValue(enemy.statuses, "strength", -stolen); addValue(state.statuses, "strength", stolen); } break;
    case "even-echo": setValue(state.statuses, "evenEcho", n); break;
    case "damage-if-debuff": if (enemy && ["weak", "vulnerable", "burn", "poison", "mark", "fracture"].some((key) => value(enemy.statuses, key) > 0)) hurtEnemy(state, enemy, calculateDamage(state, n, enemy, scalar)); break;
    case "copy-last": {
      if (combat.lastCardId) {
        const copy = makeCardInstance(state, combat.lastCardId, false, true);
        (copy as CardInstance & { costDelta?: number; forcedExhaust?: boolean }).costDelta = n;
        (copy as CardInstance & { costDelta?: number; forcedExhaust?: boolean }).forcedExhaust = true;
        combat.hand.push(copy);
      }
      break;
    }
    case "exhaust-draw": setValue(state.statuses, "exhaustDraw", n); break;
    case "persistent-fracture": setValue(state.statuses, "persistentFracture", 1); setValue(state.statuses, "damagePenalty", n); break;
    case "horizon": case "horizon-plus": {
      const total = combat.enemies.reduce((sum, entry) => sum + value(entry.statuses, "fracture"), 0);
      const repeats = Math.min(id === "horizon-plus" ? 5 : 4, Math.floor(total / Math.max(1, n)));
      for (let i = 0; i < repeats; i += 1) for (const foe of combat.enemies.filter((entry) => entry.alive)) hurtEnemy(state, foe, calculateDamage(state, id === "horizon-plus" ? 10 : 8, foe, scalar));
      break;
    }
    case "ammo-extra-hit": case "ammo-extra-hits": if (enemy && value(state.statuses, "ammo") > 0) { spendResource(state, "ammo", 1); const hits = id === "ammo-extra-hits" ? n : 1; for (let i = 0; i < hits; i += 1) hurtEnemy(state, enemy, calculateDamage(state, id === "ammo-extra-hits" ? 4 : 3, enemy, scalar)); } break;
    case "area-if-mark": if (combat.enemies.some((entry) => value(entry.statuses, "mark") > 0)) for (const foe of combat.enemies.filter((entry) => entry.alive)) hurtEnemy(state, foe, calculateDamage(state, n, foe, scalar)); break;
    case "combat-gold": addValue(state.statuses, "combatGold", n); break;
    case "piercing-damage": if (enemy) hurtEnemy(state, enemy, calculateDamage(state, n, enemy, scalar), true); break;
    case "ammo-bonus-damage": if (enemy && value(state.statuses, "ammo") > 0) { spendResource(state, "ammo", 1); hurtEnemy(state, enemy, calculateDamage(state, n, enemy, scalar)); } break;
    case "first-attack-bonus": setValue(state.statuses, "firstAttackBonus", n); break;
    case "ammo-repeat": if (value(state.statuses, "ammo") >= n) { spendResource(state, "ammo", n); for (const foe of combat.enemies.filter((entry) => entry.alive)) hurtEnemy(state, foe, calculateDamage(state, 7, foe, scalar)); } break;
    case "draw-if-third": if (combat.cardsPlayedThisTurn === 3) drawCards(state, n); break;
    case "set-ammo": setValue(state.statuses, "ammo", Math.max(value(state.statuses, "ammo"), n)); break;
    case "retain-hand": setValue(state.statuses, "retainHand", 1); break;
    case "railshot": if (enemy) { const ammo = spendResource(state, "ammo", value(state.statuses, "ammo")); hurtEnemy(state, enemy, calculateDamage(state, ammo * n, enemy, scalar)); } break;
    case "mark-refund": setValue(state.statuses, "markRefund", n); break;
    case "kill-chain": if (enemy) { const wasAlive = enemy.alive; hurtEnemy(state, enemy, calculateDamage(state, n, enemy, scalar)); if (wasAlive && !enemy.alive) { combat.energy += n >= 14 ? 3 : 2; setValue(state.statuses, "killChainReturn", 1); } } break;
    case "mark-hit-bonus": setValue(state.statuses, "markHitBonus", n); break;
    case "spend-command-damage": case "spend-command-damage-plus": if (enemy) { const cap = id.endsWith("plus") ? 3 : 2; const spent = spendResource(state, "command", cap); hurtEnemy(state, enemy, calculateDamage(state, spent * n, enemy, scalar)); } break;
    case "spend-command-block": case "spend-command-block-plus": { const cap = id.endsWith("plus") ? 3 : 2; const spent = spendResource(state, "command", cap); state.block += Math.floor(spent * n * scalar); break; }
    case "artifact-if-no-attack": if (combat.attacksPlayedThisTurn === 0) applyStatus({ statuses: state.statuses }, "artifact", n); break;
    case "skill-resonance": setValue(state.statuses, "skillResonance", n); break;
    case "command-charge": case "command-charge-plus": if (value(state.statuses, "command") >= n) { spendResource(state, "command", n); state.block += id.endsWith("plus") ? 18 : 14; } break;
    case "resonance-shield": setValue(state.statuses, "resonanceShield", n); break;
    case "redraw-hand": { const count = combat.hand.length; combat.discardPile.push(...combat.hand); combat.hand = []; drawCards(state, count + n); break; }
    case "type-triad": setValue(state.statuses, "typeTriad", n + 1); break;
    case "all-hands": case "all-hands-plus": { const command = value(state.statuses, "command"); const units = Math.min(5, Math.floor(command / Math.max(1, n))); state.block += units * (id.endsWith("plus") ? 5 : 4); drawCards(state, units); spendResource(state, "command", command); break; }
    case "all-command-damage": if (enemy) { const command = value(state.statuses, "command"); hurtEnemy(state, enemy, calculateDamage(state, command * n, enemy, scalar)); spendResource(state, "command", command); } break;
    case "resonance-per-enemy": addValue(state.statuses, "resonance", combat.enemies.filter((entry) => entry.alive).length * n); break;
    default: break;
  }
}

function resolveEffects(state: GameState, effects: Effect[], targetId?: string, scalar = 1): void {
  for (const effect of effects) {
    if (state.screen === "defeat") break;
    switch (effect.kind) {
      case "damage": resolveDamage(state, effect, targetId, scalar); break;
      case "block": {
        const bonus = effect.bonusPer ? value(state.statuses, effect.bonusPer) * (effect.bonusMultiplier ?? 1) : 0;
        state.block += Math.max(0, Math.floor((effect.amount + value(state.statuses, "dexterity") + bonus) * scalar));
        break;
      }
      case "draw": drawCards(state, Math.max(0, Math.floor(effect.amount * scalar))); break;
      case "energy": state.combat!.energy += effect.amount; break;
      case "heal": state.hp = Math.min(state.maxHp, state.hp + Math.max(1, Math.floor(effect.amount * scalar))); break;
      case "loseHp": hurtPlayer(state, Math.max(1, Math.floor(effect.amount * scalar)), "Blood price", true); break;
      case "status": {
        const targets = effect.target === "self" ? [{ statuses: state.statuses }] : effect.target === "all-enemies" ? state.combat!.enemies.filter((enemy) => enemy.alive) : [targetEnemy(state, targetId)].filter(Boolean) as CombatantState[];
        let statusAmount = Math.max(1, Math.floor(effect.amount * scalar));
        if (effect.status === "fracture" && effect.target !== "self" && hasRelic(state, "split-lens") && value(state.statuses, "splitLensTriggered") === 0) {
          statusAmount *= 2;
          setValue(state.statuses, "splitLensTriggered", 1);
          state.combat!.log.unshift("The Split Lens doubles the first Fracture.");
        }
        for (const target of targets) applyStatus(target, effect.status, statusAmount);
        if (effect.status === "burn" && effect.target === "self" && hasRelic(state, "furnace-heart") && value(state.statuses, "furnaceTriggered") === 0) {
          addValue(state.statuses, "plating", 4);
          setValue(state.statuses, "furnaceTriggered", 1);
          state.combat!.log.unshift("The Furnace Heart converts heat into 4 Plating.");
        }
        break;
      }
      case "consume": {
        const target = effect.target === "self" ? { statuses: state.statuses } : targetEnemy(state, targetId);
        if (target) addValue(target.statuses, effect.status, -(effect.amount ?? value(target.statuses, effect.status)));
        break;
      }
      case "discard": {
        const amount = Math.min(effect.amount, state.combat!.hand.length);
        state.combat!.discardPile.push(...state.combat!.hand.splice(0, amount));
        break;
      }
      case "exhaustRandom": {
        const amount = Math.min(effect.amount, state.combat!.hand.length);
        state.combat!.exhaustPile.push(...state.combat!.hand.splice(0, amount));
        if (value(state.statuses, "exhaustDraw") > 0) drawCards(state, Math.min(amount, value(state.statuses, "exhaustDraw")));
        break;
      }
      case "addCard": for (let i = 0; i < (effect.amount ?? 1); i += 1) { const card = makeCardInstance(state, effect.cardId, false, true); if (effect.destination === "hand") state.combat!.hand.push(card); else if (effect.destination === "draw") state.combat!.drawPile.push(card); else state.combat!.discardPile.push(card); } break;
      case "gold": state.gold += effect.amount; break;
      case "upgradeRandom": {
        const rng = stream(state.seed, `combat-upgrade-${state.act}-${state.floor}-${state.combat!.turn}`);
        const candidates = shuffle(state.combat!.hand.filter((card) => !card.upgraded), rng).slice(0, effect.amount);
        for (const card of candidates) card.upgraded = true;
        break;
      }
      case "special": resolveSpecial(state, effect.id, effect.value, targetId, scalar); break;
      default: break;
    }
  }
}

function cardCost(state: GameState, card: CardInstance): number {
  const def = CARDS[card.cardId];
  let cost = def.cost;
  if (def.type === "power" && hasRelic(state, "hollow-star")) cost -= 1;
  cost += Number((card as CardInstance & { costDelta?: number }).costDelta ?? 0);
  return Math.max(0, cost);
}

function checkTriad(state: GameState): void {
  if (value(state.statuses, "typeTriad") <= 0) return;
  const combat = state.combat!;
  const mask = (combat.attacksPlayedThisTurn > 0 ? 1 : 0) | (combat.skillsPlayedThisTurn > 0 ? 2 : 0) | (combat.powersPlayedThisTurn > 0 ? 4 : 0);
  const repeated = value(state.statuses, "typeTriad") > 1;
  if (mask === 7 && (repeated || value(state.statuses, "triadTriggered") === 0)) {
    combat.energy += 1;
    drawCards(state, 1);
    setValue(state.statuses, "triadTriggered", 1);
  }
}

export function playCard(input: GameState, cardUid: string, targetId?: string): GameState {
  const state = clone(input);
  const combat = state.combat;
  if (!combat || combat.ended) return state;
  const index = combat.hand.findIndex((entry) => entry.uid === cardUid);
  if (index < 0) return state;
  const instance = combat.hand[index];
  const card = CARDS[instance.cardId];
  if (!card || card.type === "status" || card.type === "curse") return state;
  const target = targetEnemy(state, targetId);
  if (card.target === "enemy" && !target) return state;
  const cost = cardCost(state, instance);
  if (combat.energy < cost) {
    state.message = "Not enough Energy.";
    return state;
  }

  combat.energy -= cost;
  combat.hand.splice(index, 1);
  combat.cardsPlayedThisTurn += 1;
  state.stats.cardsPlayed += 1;
  if (card.type === "attack") combat.attacksPlayedThisTurn += 1;
  if (card.type === "skill") combat.skillsPlayedThisTurn += 1;
  if (card.type === "power") combat.powersPlayedThisTurn += 1;
  combat.log.unshift(`${card.name}${instance.upgraded ? "+" : ""} resolves.`);

  if (card.type === "skill" && value(state.statuses, "skillResonance") > value(state.statuses, "skillResonanceUsed")) {
    addValue(state.statuses, "resonance", 1);
    addValue(state.statuses, "skillResonanceUsed", 1);
  }

  const effects = instance.upgraded ? card.upgradedEffects : card.effects;
  resolveEffects(state, effects, target?.id);
  checkTriad(state);

  const forcedExhaust = Boolean((instance as CardInstance & { forcedExhaust?: boolean }).forcedExhaust);
  if (card.type !== "power") {
    if (card.id === "kill-chain" && value(state.statuses, "killChainReturn") > 0) {
      combat.hand.push(instance);
      setValue(state.statuses, "killChainReturn", 0);
    } else if (card.exhaust || forcedExhaust) {
      combat.exhaustPile.push(instance);
      if (hasRelic(state, "votive-wire")) for (const foe of combat.enemies.filter((enemy) => enemy.alive)) hurtEnemy(state, foe, 2);
      if (value(state.statuses, "exhaustDraw") > value(state.statuses, "exhaustDrawUsed")) {
        drawCards(state, 1);
        addValue(state.statuses, "exhaustDrawUsed", 1);
      }
    } else combat.discardPile.push(instance);
  }

  if (value(state.statuses, "echo") > 0 && card.type !== "power" && card.id !== "paradox") {
    addValue(state.statuses, "echo", -1);
    combat.log.unshift(`Echo repeats ${card.name} at half force.`);
    resolveEffects(state, effects, target?.id, 0.5);
  }
  combat.lastCardId = card.id;

  if (hasRelic(state, "gasket-ring") && combat.skillsPlayedThisTurn > 0 && combat.skillsPlayedThisTurn % 3 === 0) state.block += 4;
  if (hasRelic(state, "wake-bell") && state.stats.cardsPlayed % 9 === 0) { combat.energy += 2; drawCards(state, 2); }

  checkCombatEnd(state);
  return state;
}

function enemyDamageToPlayer(state: GameState, enemy: CombatantState, base: number): number {
  let amount = Math.max(0, base + value(enemy.statuses, "strength"));
  if (value(enemy.statuses, "weak") > 0) amount = Math.floor(amount * 0.75);
  if (value(state.statuses, "vulnerable") > 0) amount = Math.floor(amount * 1.5);
  if ((value(enemy.statuses, "mark") > 0 || value(enemy.statuses, "fracture") > 0) && hasRelic(state, "red-thread")) amount = Math.max(0, amount - 1);
  const tier = ENEMIES[enemy.definitionId].tier;
  const encounterScale = tier === "normal" ? 0.78 : tier === "elite" ? 0.78 : 0.68;
  const act = ENEMIES[enemy.definitionId].act;
  const actScale = act === 1 ? 1 : act === 2 ? 0.87 : 0.75;
  const difficultyScale = state.settings.difficulty === "story" ? 0.75 : state.settings.difficulty === "ascendant" ? 1.2 : 1;
  return Math.max(0, Math.floor(amount * encounterScale * actScale * difficultyScale));
}

function advanceIntent(state: GameState, enemy: CombatantState): void {
  const def = ENEMIES[enemy.definitionId];
  if (!enemy.alive) return;
  if (def.pattern === "random") {
    const rng = stream(state.seed, `intent-${state.act}-${state.floor}-${enemy.id}-${state.combat!.turn}`);
    let next = randomInt(0, def.intents.length - 1, rng);
    if (next === enemy.intentIndex && def.intents.length > 1) next = (next + 1) % def.intents.length;
    enemy.intentIndex = next;
  } else enemy.intentIndex = (enemy.intentIndex + 1) % def.intents.length;
  enemy.intent = def.intents[enemy.intentIndex];
}

function runEnemyTurn(state: GameState, enemy: CombatantState): void {
  if (!enemy.alive || state.screen === "defeat") return;
  enemy.block = 0;
  const intent = enemy.intent;
  if (enemy.definitionId === "vaultmouth" && intent.id === "open") enemy.block = 0;
  if (intent.block) enemy.block += intent.block;
  if (intent.status) {
    for (const status of intent.status) {
      const target = status.target === "self" ? enemy : { statuses: state.statuses };
      applyStatus(target, status.status, status.amount);
    }
  }
  if (intent.damage) {
    const hits = intent.hits ?? 1;
    for (let hit = 0; hit < hits; hit += 1) {
      const amount = enemyDamageToPlayer(state, enemy, intent.damage);
      const lost = hurtPlayer(state, amount, `${enemy.name} — ${intent.label}`);
      if (lost > 0 && value(state.statuses, "counter") > 0) hurtEnemy(state, enemy, value(state.statuses, "counter"), true);
      if (value(state.statuses, "thorns") > 0) hurtEnemy(state, enemy, value(state.statuses, "thorns"), true);
      if (state.hp <= 0) break;
    }
  }
  if (value(enemy.statuses, "burn") > 0) { hurtEnemy(state, enemy, value(enemy.statuses, "burn"), true); addValue(enemy.statuses, "burn", -1); }
  if (value(enemy.statuses, "poison") > 0) { hurtEnemy(state, enemy, value(enemy.statuses, "poison"), true); addValue(enemy.statuses, "poison", -1); }
  addValue(enemy.statuses, "weak", -1);
  addValue(enemy.statuses, "vulnerable", -1);
  advanceIntent(state, enemy);
}

export function endTurn(input: GameState): GameState {
  const state = clone(input);
  const combat = state.combat;
  if (!combat || combat.ended) return state;

  const retained: CardInstance[] = [];
  for (const card of combat.hand) {
    const def = CARDS[card.cardId];
    if (def.id === "status-burn") hurtPlayer(state, 2, "System Burn", true);
    if (def.ethereal) combat.exhaustPile.push(card);
    else if (def.retain || value(state.statuses, "retainHand") > 0) retained.push(card);
    else combat.discardPile.push(card);
  }
  combat.hand = retained;
  setValue(state.statuses, "retainHand", 0);

  if (value(state.statuses, "burn") > 0) { hurtPlayer(state, value(state.statuses, "burn"), "Burn", true); addValue(state.statuses, "burn", -1); }
  if (value(state.statuses, "poison") > 0) { hurtPlayer(state, value(state.statuses, "poison"), "Poison", true); addValue(state.statuses, "poison", -1); }
  if (value(state.statuses, "regeneration") > 0) { state.hp = Math.min(state.maxHp, state.hp + value(state.statuses, "regeneration")); addValue(state.statuses, "regeneration", -1); }
  if (value(state.statuses, "resonanceShield") > 0) {
    state.block += Math.floor(value(state.statuses, "resonance") * value(state.statuses, "resonanceShield") / 100);
    setValue(state.statuses, "resonance", 0);
  }

  for (const enemy of combat.enemies) runEnemyTurn(state, enemy);
  checkCombatEnd(state);
  if (combat.ended || state.screen === "defeat") return state;

  const retention = value(state.statuses, "blockRetention");
  state.block = retention > 0 ? Math.floor(state.block * retention / 100) : 0;
  const plating = value(state.statuses, "plating") + value(state.statuses, "persistentPlating");
  if (plating > 0) state.block += plating;
  setValue(state.statuses, "plating", value(state.statuses, "persistentPlating"));
  setValue(state.statuses, "counter", 0);
  addValue(state.statuses, "weak", -1);
  addValue(state.statuses, "vulnerable", -1);

  combat.turn += 1;
  combat.energy = combat.maxEnergy;
  combat.cardsPlayedThisTurn = 0;
  combat.attacksPlayedThisTurn = 0;
  combat.skillsPlayedThisTurn = 0;
  combat.powersPlayedThisTurn = 0;
  setValue(state.statuses, "markRefundUsed", 0);
  setValue(state.statuses, "skillResonanceUsed", 0);
  setValue(state.statuses, "exhaustDrawUsed", 0);
  if (value(state.statuses, "typeTriad") > 1) setValue(state.statuses, "triadTriggered", 0);
  if (value(state.statuses, "evenEcho") >= combat.turn && combat.turn % 2 === 0) addValue(state.statuses, "echo", 1);
  if (hasRelic(state, "bone-radio") && combat.turn === 3) { combat.energy += 1; drawCards(state, 2); }
  drawCards(state, 5 - combat.hand.length);
  state.energy = combat.energy;
  state.message = `Turn ${combat.turn}. Enemy intents updated.`;
  return state;
}

function randomRelic(state: GameState, preferred?: RelicInstance["id"] | "common" | "uncommon" | "rare"): string {
  const owned = new Set(state.relics.map((relic) => relic.id));
  let pool = Object.values(RELICS).filter((relic) => relic.rarity !== "starter" && relic.rarity !== "boss" && !owned.has(relic.id));
  if (preferred && ["common", "uncommon", "rare"].includes(preferred)) {
    const filtered = pool.filter((relic) => relic.rarity === preferred);
    if (filtered.length) pool = filtered;
  }
  return pick(pool.length ? pool : Object.values(RELICS).filter((relic) => relic.rarity !== "starter"), stream(state.seed, `relic-${state.act}-${state.floor}-${state.relics.length}`)).id;
}

function cardReward(state: GameState, count = 3): string[] {
  const pool = CLASS_CARD_POOLS[state.classId];
  const rng = stream(state.seed, `reward-${state.act}-${state.floor}-${state.stats.combatsWon}`);
  const candidates = shuffle(pool, rng);
  const selected: string[] = [];
  while (selected.length < count && candidates.length) {
    const rarity = weightedPick([
      { item: "common", weight: 60 },
      { item: "uncommon", weight: 32 },
      { item: "rare", weight: 8 + state.act * 2 },
    ], rng);
    const index = candidates.findIndex((id) => CARDS[id].rarity === rarity);
    selected.push(candidates.splice(index >= 0 ? index : 0, 1)[0]);
  }
  return selected;
}

function buildReward(state: GameState): RewardState {
  const type = state.combat!.nodeType;
  const rng = stream(state.seed, `gold-${state.act}-${state.floor}`);
  const gold = type === "boss" ? 90 : type === "elite" ? randomInt(38, 55, rng) : randomInt(18, 29, rng);
  return {
    cards: cardReward(state),
    gold,
    relicId: type === "elite" ? randomRelic(state) : type === "boss" ? "last-witness" : undefined,
    claimedGold: false,
    claimedRelic: false,
  };
}

function checkCombatEnd(state: GameState): void {
  const combat = state.combat;
  if (!combat || combat.ended) return;
  if (state.hp <= 0) {
    combat.ended = true;
    combat.victory = false;
    state.screen = "defeat";
    return;
  }
  if (combat.enemies.every((enemy) => !enemy.alive)) {
    combat.ended = true;
    combat.victory = true;
    state.stats.combatsWon += 1;
    if (combat.nodeType === "elite") state.stats.elitesWon += 1;
    if (combat.nodeType === "boss") state.stats.bossesWon += 1;
    const bonusGold = value(state.statuses, "combatGold") + (combat.nodeType === "elite" && hasRelic(state, "pale-coin") ? 18 : 0);
    state.reward = buildReward(state);
    state.reward.gold += bonusGold;
    state.screen = "reward";
    state.message = "The route is clear. Choose what the Wake leaves behind.";
  }
}

export function takeRewardCard(input: GameState, cardId?: string): GameState {
  const state = clone(input);
  if (cardId && state.reward?.cards.includes(cardId)) {
    state.deck.push(makeCardInstance(state, cardId));
    state.stats.cardsAdded += 1;
  }
  return leaveReward(state);
}

export function leaveReward(input: GameState): GameState {
  const state = clone(input);
  if (!state.reward || !state.combat) return state;
  state.gold += state.reward.gold;
  state.stats.goldEarned += state.reward.gold;
  if (state.reward.relicId && !state.relics.some((relic) => relic.id === state.reward!.relicId)) state.relics.push({ id: state.reward.relicId });
  const wasBoss = state.combat.nodeType === "boss";
  completeCurrentNode(state);
  state.reward = undefined;
  state.combat = undefined;
  state.statuses = {};
  state.block = 0;
  if (wasBoss) {
    if (state.act >= 3) {
      state.screen = "victory";
      state.message = "The Meridian waits for your verdict.";
      return state;
    }
    state.act += 1;
    state.hp = Math.min(state.maxHp, state.hp + Math.ceil(state.maxHp * 0.25));
    state.map = generateMap(state.seed, state.act);
  }
  state.screen = "map";
  state.message = wasBoss ? `Act ${state.act}: ${ACT_NAMES[state.act]}.` : "Choose the next route.";
  return state;
}

function generateShop(state: GameState): ShopItem[] {
  const rng = stream(state.seed, `shop-${state.act}-${state.floor}`);
  const pool = shuffle(CLASS_CARD_POOLS[state.classId], rng).slice(0, 5);
  const items: ShopItem[] = pool.map((cardId, index) => {
    const rarity = CARDS[cardId].rarity;
    const base = rarity === "rare" ? 145 : rarity === "uncommon" ? 88 : 52;
    return { id: `shop-card-${index}`, kind: "card", referenceId: cardId, price: base + randomInt(-8, 8, rng), sold: false };
  });
  const relicId = randomRelic(state);
  items.push({ id: "shop-relic", kind: "relic", referenceId: relicId, price: RELICS[relicId].rarity === "rare" ? 205 : 158, sold: false });
  items.push({ id: "shop-heal", kind: "heal", price: 48, sold: false });
  items.push({ id: "shop-remove", kind: "remove", price: state.removeCost, sold: false });
  return items;
}

function weakestCardIndex(state: GameState): number {
  const starter = state.deck.findIndex((card) => CARDS[card.cardId].rarity === "starter" && !card.upgraded);
  return starter >= 0 ? starter : 0;
}

export function buyShopItem(input: GameState, itemId: string): GameState {
  const state = clone(input);
  const item = state.shop?.find((entry) => entry.id === itemId);
  if (!item || item.sold || state.gold < item.price) return state;
  state.gold -= item.price;
  item.sold = true;
  if (item.kind === "card" && item.referenceId) { state.deck.push(makeCardInstance(state, item.referenceId)); state.stats.cardsAdded += 1; }
  if (item.kind === "relic" && item.referenceId) state.relics.push({ id: item.referenceId });
  if (item.kind === "heal") state.hp = Math.min(state.maxHp, state.hp + Math.ceil(state.maxHp * 0.28));
  if (item.kind === "remove") {
    const index = weakestCardIndex(state);
    if (index >= 0) { state.deck.splice(index, 1); state.stats.cardsRemoved += 1; state.removeCost += 25; }
  }
  return state;
}

export function leaveNode(input: GameState): GameState {
  const state = clone(input);
  completeCurrentNode(state);
  state.shop = undefined;
  state.eventId = undefined;
  state.pendingTreasure = undefined;
  state.screen = "map";
  state.message = "Choose the next route.";
  return state;
}

export function restHeal(input: GameState): GameState {
  const state = clone(input);
  state.hp = Math.min(state.maxHp, state.hp + Math.ceil(state.maxHp * 0.25));
  return leaveNode(state);
}

export function restUpgrade(input: GameState, cardUid: string): GameState {
  const state = clone(input);
  const card = state.deck.find((entry) => entry.uid === cardUid);
  if (card) card.upgraded = true;
  return leaveNode(state);
}

export function claimTreasure(input: GameState): GameState {
  const state = clone(input);
  if (state.pendingTreasure && !state.relics.some((relic) => relic.id === state.pendingTreasure)) state.relics.push({ id: state.pendingTreasure });
  return leaveNode(state);
}

function addRandomRare(state: GameState): void {
  const rare = CLASS_CARD_POOLS[state.classId].filter((id) => CARDS[id].rarity === "rare");
  state.deck.push(makeCardInstance(state, pick(rare, stream(state.seed, `event-rare-${state.floor}`))));
  state.stats.cardsAdded += 1;
}

function upgradeRandomDeck(state: GameState, count: number): void {
  const rng = stream(state.seed, `event-upgrade-${state.floor}`);
  for (const card of shuffle(state.deck.filter((entry) => !entry.upgraded), rng).slice(0, count)) card.upgraded = true;
}

function addRandomRelic(state: GameState): void {
  const relicId = randomRelic(state);
  if (!state.relics.some((relic) => relic.id === relicId)) state.relics.push({ id: relicId });
}

export function chooseEvent(input: GameState, choiceId: string): GameState {
  const state = clone(input);
  const event = state.eventId ? EVENTS[state.eventId] : undefined;
  const choice: EventChoice | undefined = event?.choices.find((entry) => entry.id === choiceId);
  if (!choice) return state;
  for (const effect of choice.effect) {
    switch (effect.kind) {
      case "heal": state.hp = Math.min(state.maxHp, state.hp + (effect.amount ?? 0)); break;
      case "damage": state.hp = Math.max(1, state.hp - (effect.amount ?? 0)); break;
      case "gold": state.gold = Math.max(0, state.gold + (effect.amount ?? 0)); break;
      case "maxHp": state.maxHp = Math.max(1, state.maxHp + (effect.amount ?? 0)); state.hp = Math.min(state.maxHp, state.hp + Math.max(0, effect.amount ?? 0)); break;
      case "curse": if (effect.referenceId) { state.deck.push(makeCardInstance(state, effect.referenceId)); state.stats.cardsAdded += 1; } break;
      case "relic": addRandomRelic(state); break;
      case "remove": { const index = weakestCardIndex(state); if (index >= 0) { state.deck.splice(index, 1); state.stats.cardsRemoved += 1; } break; }
      case "card": {
        if (effect.referenceId === "rare") addRandomRare(state);
        else if (effect.referenceId === "upgrade-two") upgradeRandomDeck(state, 2);
        else if (effect.referenceId === "upgrade-three") upgradeRandomDeck(state, 3);
        else if (effect.referenceId === "rare-or-relic") stream(state.seed, `event-coin-${state.floor}`)() < 0.5 ? addRandomRare(state) : addRandomRelic(state);
        break;
      }
      default: break;
    }
  }
  return leaveNode(state);
}

export function abandonRun(input: GameState): GameState {
  const state = clone(input);
  state.screen = "title";
  state.combat = undefined;
  return state;
}

export function getCardCost(state: GameState, card: CardInstance): number { return cardCost(state, card); }
export function getLivingEnemies(state: GameState): CombatantState[] { return state.combat?.enemies.filter((enemy) => enemy.alive) ?? []; }
export function canEnterNode(state: GameState, node: MapNode): boolean { return node.accessible && !node.visited; }
